<template>
    <DCard padding>
        <Search
            v-model:search="filter.search"
            v-model:date-from="filter.dateFrom"
            v-model:date-to="filter.dateTo"
            @filter="applyFilter"
        />
        <hr>
        <div class="collection-history-table">
            <header>
                <div>{{ __('tools.collection.headers.file') }}</div>
                <div>{{ __('tools.collection.headers.date') }}</div>
                <div>{{ __('tools.collection.headers.invoice_id') }}</div>
                <div>{{ __('tools.collection.headers.customer_id') }}</div>
                <div>{{ __('tools.collection.headers.company_name') }}</div>
                <div>{{ __('tools.collection.headers.customer_firstname') }}</div>
                <div>{{ __('tools.collection.headers.customer_lastname') }}</div>
                <div>{{ __('tools.collection.headers.invoice_amount') }}</div>
                <div>{{ __('tools.collection.headers.actions') }}</div>
            </header>
            <main>
                <div
                    v-for="collection in collections.data"
                    :key="collection.id"
                    class="item-group"
                >
                    <div class="item-header">
                        <div>
                            <DButton
                                mini
                                :icon="!collapsedGroups.includes(collection.id) ? 'arrow-down' : 'arrow-up'"
                                @click="toggleGroup(collection.id)"
                            />
                        </div>
                        <div>{{ collection.file }}</div>
                        <div>{{ collection.date }}</div>
                        <div />
                        <div />
                        <div />
                        <div />
                        <div />
                        <div />
                        <div>
                            <DButton
                                icon="download"
                                mini
                                :label="__('tools.collection.actions.download_csv')"
                                @click="downloadCsv(collection)"
                            />
                            <!-- v-c-tooltip renders an empty bubble for '' and never re-reads its value, so only the disabled button gets it. -->
                            <DButton
                                v-if="collection.is_zip_available === false"
                                v-c-tooltip="__('tools.collection.zip_unavailable')"
                                icon="download"
                                mini
                                :disabled="true"
                                :label="__('tools.collection.actions.download_zip')"
                            />
                            <DButton
                                v-else
                                icon="download"
                                mini
                                :label="__('tools.collection.actions.download_zip')"
                                @click="downloadZip(collection)"
                            />
                        </div>
                    </div>
                    <div
                        v-for="invoice in (invoicesByCollection[collection.id] ?? [])"
                        v-show="collapsedGroups.includes(collection.id)"
                        :key="invoice.id"
                        class="item"
                    >
                        <div />
                        <div />
                        <div>{{ invoice.date }}</div>
                        <div v-if="userPermissions.includes('invoice.view')">
                            <Link :href="showInvoiceRoute(invoice)">
                                {{ invoice.id }}
                            </Link>
                        </div>
                        <div v-else>
                            {{ invoice.id }}
                        </div>
                        <div v-if="userPermissions.includes('customer.view')">
                            <Link :href="customerInvoicesRoute(invoice)">
                                {{ invoice.customer.id }}
                            </Link>
                        </div>
                        <div v-else>
                            {{ invoice.customer.id }}
                        </div>
                        <div>{{ invoice.customer.company }}</div>
                        <div>{{ invoice.customer.firstname }}</div>
                        <div>{{ invoice.customer.lastname }}</div>
                        <div>{{ priceFormatForDisplay(invoice.total_amount, 2, invoice.customer.market_region_id) }}</div>
                    </div>
                </div>
            </main>
        </div>
        <DPagination
            v-if="collections.last_page > 1"
            class="pb-2"
            :active-page="collections.current_page"
            :pages="collections.last_page"
            align="center"
            @update:activePage="changePage"
        />
    </DCard>
</template>

<script>
import App from '~@/App.vue'
import CollectionLayout from '~@/Pages/Tools/Collection/Layout.vue'
import Search from '~@/Components/Tools/Collection/Search.vue'
import priceFormat from '~@/Mixins/priceFormat'
export default {
    name: 'CollectionHistory',
    components: { Search },
    mixins: [
        priceFormat
    ],
    layout: [App, CollectionLayout],
    props: {
        collections: {
            type: Object,
            default: () => { return {} }
        },
        appliedFilters: {
            type: Object,
            default: () => { return {} }
        },
        isCollectionPartnerVisma: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            collapsedGroups: [],
            invoicesByCollection: {},
            filter: {
                search: this.appliedFilters.search,
                dateFrom: this.appliedFilters.dateFrom,
                dateTo: this.appliedFilters.dateTo
            }
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        }
    },
    methods: {
        showInvoiceRoute (invoice) {
            return this.$route('customers.customers.invoices.show', {
                customer: invoice.customer.id,
                invoice: invoice.id
            })
        },
        customerInvoicesRoute (invoice) {
            return this.$route('customers.customers.invoices.index', { customer: invoice.customer.id })
        },
        changePage (page) {
            const queryParameters = Object.fromEntries(Object.entries(this.filter).filter(([_, v]) => v))

            if (page !== 1) {
                queryParameters.page = page
            }

            this.$inertia.get(this.$route('tools.collection.history'), queryParameters)
        },
        downloadCsv (item) {
            const link = document.createElement('a')
            link.href = this.$route('tools.collection.download-csv', { collection: item.id })
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        downloadZip (item) {
            const link = document.createElement('a')
            link.href = this.$route('tools.collection.download-zip', { collection: item.id })
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        toggleGroup (collectionId) {
            if (this.collapsedGroups.includes(collectionId)) {
                this.collapsedGroups = this.collapsedGroups.filter(e => e !== collectionId)

                return
            }

            this.collapsedGroups.push(collectionId)
            this.loadInvoices(collectionId)
        },
        loadInvoices (collectionId) {
            // Fetched once per handover and kept, so collapsing and expanding again is free.
            if (this.invoicesByCollection[collectionId] !== undefined) {
                return
            }

            const collection = this.collections.data.find(item => item.id === collectionId)

            // The page ships the count, so a handover without invoices needs no request.
            if (collection?.invoices_count === 0) {
                this.invoicesByCollection[collectionId] = []

                return
            }

            window.axios.get(this.$route('tools.collection.history-invoices', { collection: collectionId }))
                .then(response => {
                    this.invoicesByCollection[collectionId] = response.data
                })
                .catch(() => {
                    // Leave the entry unset so re-expanding retries, and collapse the group again
                    // rather than showing it empty as though the handover had no invoices.
                    this.collapsedGroups = this.collapsedGroups.filter(e => e !== collectionId)
                })
        },
        applyFilter () {
            const queryParameters = Object.fromEntries(Object.entries(this.filter).filter(([_, v]) => v))

            this.$inertia.get(this.$route('tools.collection.history'), queryParameters)
        }
    }
}
</script>

<style lang="scss" scoped>
$tablecolumns: 50px 1fr  100px  150px  120px  1fr  1fr 1fr 185px 315px;

.collection-history-table .exclude-checkbox {
    margin-bottom: 23px;
}
.collection-history-table header {
    border-bottom: 1px solid rgba(0,0,0,0.14);
    display: grid;
    grid-template-columns: $tablecolumns;
    grid-gap: 10px;
}
.collection-history-table header > div:nth-child(1) {
    grid-column: 2;
}
.collection-history-table header > div {
    padding: 10px;
    font-weight: bold;
}
.collection-history-table main {
}
.collection-history-table main .item-group {
    border-bottom: 1px solid rgba(0,0,0,0.3);
    position: relative;
}
.collection-history-table main .item-group .item-header {
    display: grid;
    grid-template-columns: $tablecolumns;
    grid-gap: 10px;
    background: #fff;
    border-bottom: 1px solid rgba(0,0,0,0.14);
    position: sticky;
    top: -25px;
    width: 100%;
    z-index: 100;
}
.collection-history-table main .item-group .item-header > div {
    padding: 10px;
    align-self: center;
}
.collection-history-table main .item-group .item {
    display: grid;
    grid-template-columns: $tablecolumns;
    grid-gap: 10px;
}
.collection-history-table main .item-group .item > div {
    padding: 10px;
    align-self: center;
}
.collection-history-table main .item-group .item > div:nth-child(1) {
    position: relative;
    overflow: hidden;
    padding: 0;
    height: 100%;
}
.collection-history-table main .item-group .item > div:nth-child(1)::after {
    content: "";
    display: block;
    position: absolute;
    left: 21px;
    top: 0;
    width: 2px;
    height: 60px;
    background: rgba(0,0,0,0.07);
}
.collection-history-table main .item-group .item > div:nth-child(1)::before {
    content: "";
    display: block;
    position: absolute;
    left: 23px;
    top: 25px;
    width: 10px;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    height: 2px;
    background: rgba(0,0,0,0.07);
}
.collection-history-table main .item-group .item:last-of-type > div:nth-child(1)::after {
    height: 35px;
    background: rgba(0,0,0,0.07);
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
}
.collection-history-table main .item-group .item:hover {
    background: rgba(0,0,0,0.07);
}
</style>
